// Packages and components
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box } from 'grommet';
import { Configure, ShareRounded } from 'grommet-icons';
import CameraSelector from './CameraSelector';
import CopyToClipboard from 'react-copy-to-clipboard';
import EnableCameraModal from './Modals/EnableCameraModal';
import GoProModal from './Modals/GoProModal';
import MevoModal from './Modals/MevoModal';
import SHDButton from '@ui/Button/SHDButton';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDText from '@ui/Text/SHDText';
import Toast from '@components/Toast';
import UserCannotModal from './Modals/UserCannotModal';

// Helpers and utilities
import { AppHostContext } from '@services/appHost';
import { isMobile } from '@helpers/browserDetect';
import { SIDELINE_APP_URLS } from '@constants/strings';
import { UI } from '@constants/baseUrls';
import { useModal } from '@components/ModalContext';

// Hooks
import { useRtmpKey } from '@hooks/useRtmpKey';
import { useTeamCapture } from '@hooks/useCapture';

// Types and interfaces
import type { shd } from '@shd/jslib/models';
import { CaptureStatus, CaptureEquipmentName } from './camera-selection.d';
import type { CameraSelectionSheetProps } from './schedule.d';
import type { Dict } from '@theme/theme.d';
import { analytics } from '@services/analytics';
import axios from '@helpers/axios';

export const CameraSelectionSheet: React.FC<CameraSelectionSheetProps> = ({
  teamId,
  teamName,
  game,
  onClick,
  onCloseTray,
}) => {
  // Contexts
  const appHost = useContext(AppHostContext);

  // Hooks
  const { getStatusKey, getRtmpKey } = useRtmpKey();
  const { getTeamCapture } = useTeamCapture();
  const { showModal, hideModal } = useModal();

  // State
  const [copied, setCopied] = useState<null | string>(null);
  const [selectedCapture, setSelectedCapture] =
    useState<shd.TeamCapture | null>(null);
  const [captures, setCaptures] = useState<shd.TeamCapture[]>([]);
  const [scoringLink, setScoringLink] = useState<string | null>(null);
  const [rtmpKey, setRtmpKey] = useState<Dict | null>(null);
  const [statusKey, setStatusKey] = useState<string | null>(null);

  // Constants
  const appHostService = appHost.isHosted ? appHost.service : undefined;
  const shdAppCapture: Partial<shd.TeamCapture> = useMemo(() => {
    return {
      _id: 'sidelinehd-app',
      captureName: CaptureEquipmentName.SIDELINE_APP,
      equipCamera: 'app',
      state_isLive: false,
      targetTeamId: teamId,
      targetTeamName: teamName,
      isOwnedByActiveUser: true,
    };
  }, [teamId, teamName]);

  const userCaptures = useMemo(() => {
    return captures.filter((capture) => capture.isOwnedByActiveUser);
  }, [captures]);

  const teamCaptures = useMemo(() => {
    return captures.filter((capture) => !capture.isOwnedByActiveUser);
  }, [captures]);

  useEffect(() => {
    if (captures.length === 0) {
      getTeamCapture(teamId).then((res) => {
        setCaptures([
          shdAppCapture as shd.TeamCapture,
          ...(res as shd.TeamCapture[]),
        ]);
      });
    }
  }, [teamId, getTeamCapture, captures, teamName, shdAppCapture]);

  useEffect(() => {
    if (!statusKey || !scoringLink) {
      getStatusKey(teamId).then((res) => {
        setScoringLink(`${UI}/${teamId}?tab=scoring&key=${res}`);
        setStatusKey(res);
      });
    }

    if (
      selectedCapture &&
      selectedCapture._id !== 'sidelinehd-app' &&
      !rtmpKey
    ) {
      getRtmpKey(selectedCapture?._id as string).then((res) => {
        setRtmpKey(res);
      });
    }
  }, [
    selectedCapture,
    getRtmpKey,
    getStatusKey,
    rtmpKey,
    statusKey,
    teamId,
    scoringLink,
  ]);

  // Handlers
  /** @desc Sets the status of the team/camera based on a variety of situations. */
  const handleGetStatus = (capture: shd.TeamCapture) => {
    const isAnyTeamManagerCaptureLive = teamCaptures.some((c) => {
      return c.state_isLive && c.targetTeamId === teamId;
    });

    if (capture.targetTeamId === 'disabled') {
      return CaptureStatus.Disabled;
    }

    if (capture.targetTeamId !== teamId) {
      return CaptureStatus.OtherTeam;
    }

    if (isAnyTeamManagerCaptureLive) {
      return CaptureStatus.TeamLive;
    }

    if (capture.state_isLive) {
      return CaptureStatus.Live;
    }

    return CaptureStatus.Ready;
  };

  /** @desc When the user selects a camera that is not enabled, this opens a modal that will enable it for them. */
  const handleEnableCamera = (
    capture: shd.TeamCapture,
    isOtherTeam: boolean
  ) => {
    showModal(
      <EnableCameraModal
        capture={capture}
        isOtherTeam={isOtherTeam}
        teamId={teamId}
        teamName={teamName}
        onClose={() => hideModal()}
        onUpdate={(updatedCapture) => {
          const newCaptures = captures.map((c) => {
            if (c._id === updatedCapture._id) {
              return updatedCapture;
            }
            if (
              c.targetTeamId === teamId &&
              c.captureName !== CaptureEquipmentName.SIDELINE_APP
            ) {
              return {
                ...c,
                targetTeamId: 'disabled',
              };
            }
            return c;
          });

          setCaptures(newCaptures);
          setSelectedCapture(updatedCapture);
          hideModal();
        }}
      />,
      {
        isConfirmation: true,
        isRelativeHeight: true,
        onClose: () => {
          hideModal();
        },
      }
    );
  };

  /** @desc When the user selects an option where they cannot proceed, a confirmation modal will appear that lets them know why. */
  const handleUserCannotProceed = (heading: string, body?: string) => {
    showModal(
      <UserCannotModal
        heading={heading}
        body={body ? body : ''}
        onClose={() => hideModal()}
      />,
      {
        isConfirmation: true,
        isRelativeHeight: true,
        onClose: () => {
          hideModal();
        },
      }
    );
  };

  /** @desc
   *  When the user selects a camera, this ensures that they able to select it before doing so.
   *  CaptureStatus.Ready: The camera is ready to be selected.
   *  CaptureStatus.OtherTeam: The camera is enabled for another team, let the user enable and then select it.
   *  CaptureStatus.Disabled: The camera is disabled, let the user enable and then select it.
   *  CaptureStatus.Live: The camera is already live, let the user know they cannot select it.
   *  CaptureStatus.TeamLive: The current team is live, let the user know they cannot select it.
   * */
  const handleCameraSelected = (capture: shd.TeamCapture) => {
    const status = handleGetStatus(capture);
    switch (status) {
      case CaptureStatus.Ready:
        setSelectedCapture(capture);
        break;
      case CaptureStatus.OtherTeam:
        handleEnableCamera(capture, true);
        break;
      case CaptureStatus.Disabled:
        handleEnableCamera(capture, false);
        break;
      case CaptureStatus.Live:
        handleUserCannotProceed(
          `Camera is already live`,
          `You cannot stream to a camera that is currently live.`
        );
        break;
      case CaptureStatus.TeamLive:
        handleUserCannotProceed(
          `Team is already live`,
          `You cannot go live with a different camera while ${capture.targetTeamName} is already live`
        );
        break;
    }
  };

  /** @desc When the user selects a camera, this will redirect them to the app or the web app. */
  const handleAppRedirect = async (
    url: string,
    shdAppTeamId: string | null = null
  ) => {
    onCloseTray();
    analytics.track('[ENG] GoLiveAppRedirect', {
      teamId: shdAppTeamId,
      appIsHost: appHost.isHosted,
      url: url,
    });
    if (appHost.isHosted) {
      if (shdAppTeamId !== null) {
        analytics.track('[ENG] GoLiveAppRedirect - Redirecting to app', {
          teamId: shdAppTeamId,
          appIsHost: appHost.isHosted,
        });
        appHost.service.notifications.appGoLive(shdAppTeamId);
      } else {
        analytics.track('[ENG] GoLiveAppRedirect - Open app link', {
          teamId: shdAppTeamId,
          appIsHost: appHost.isHosted,
          url: url,
        });
        appHostService?.notifications.openAppLink(url);
      }
    } else {
      analytics.track('[ENG] GoLiveAppRedirect - Open window location', {
        teamId: shdAppTeamId,
        appIsHost: appHost.isHosted,
        url: url,
      });
      const result = await axios.post('/api/remote_login/preauthorize', {});
      if (result) {
        analytics.track(
          '[ENG] GoLiveAppRedirect - Open window location with success result',
          {
            teamId: shdAppTeamId,
            appIsHost: appHost.isHosted,
            url: url,
            resultPreauth: result,
          }
        );
        window.location.href = url;
      }
    }
  };

  /** @desc When the user clicks "Confirm":
   *  - Check to make sure that they are on a mobile device.
   *  - If they are, redirect them to the app or the web app.
   *  - If they are not, let them know that they cannot go live from desktop.
   */
  const handleConfirmCapture = (confirmedCapture: shd.TeamCapture) => {
    // eslint-disable-next-line no-console
    console.log('nks confirmedCapture isMobile', isMobile.any());
    // eslint-disable-next-line no-console
    console.log('nks confirmedCapture appHost.isHosted', appHost.isHosted);

    const isRunningInMobileApp = appHost.isHosted;
    const isMobileBrowser = isMobile.any();

    if (!(isMobileBrowser || isRunningInMobileApp)) {
      handleUserCannotProceed(
        `You cannot go live from desktop`,
        `You can enable and point cameras, but must go live from a mobile device.`
      );
      return;
    }

    switch (confirmedCapture.equipCamera) {
      case CaptureEquipmentName.GOPRO:
        onCloseTray();
        showModal(
          <GoProModal
            appHost={appHost}
            appHostService={appHostService}
            capture={confirmedCapture}
            onClose={() => hideModal()}
          />,
          {
            onClose: () => {
              hideModal();
            },
          }
        );
        break;

      case CaptureEquipmentName.MEVO:
        onCloseTray();
        showModal(
          <MevoModal
            appHost={appHost}
            appHostService={appHostService}
            capture={confirmedCapture}
            onClose={() => hideModal()}
          />,
          {
            onClose: () => {
              hideModal();
            },
            isFixedWidth: true,
          }
        );
        break;
      case CaptureEquipmentName.MEVO_SINGLE:
        handleAppRedirect(rtmpKey?.mevoAppUrl as string);
        break;
      case CaptureEquipmentName.MEVO_MULTI:
        handleAppRedirect(rtmpKey?.mevoMulticamUrl as string);
        break;
      case CaptureEquipmentName.SMARTPHONE:
      case CaptureEquipmentName.SMARTPHONE_LEGACY:
        handleAppRedirect(rtmpKey?.larixUrl as string);
        break;
      case CaptureEquipmentName.SIDELINE_APP:
      default:
        handleAppRedirect(
          `${SIDELINE_APP_URLS.team}/${teamId}?statusKey=${statusKey}`,
          teamId
        );
        break;
    }
  };

  return (
    <>
      <SHDModalContainer data-testid="camera-selection-tray" gap="xlarge">
        <Box direction="row" justify="between" align="end">
          <Box width={{ max: '80%' }}>
            <SHDHeading level={3} color="gray-900">
              Confirm your streaming camera
            </SHDHeading>
          </Box>

          <Box
            className="hover-link"
            onClick={() => onClick('settings', game)}
            pad={{ vertical: 'xxsmall', left: 'xxsmall' }}
          >
            <Configure size="24px" color="black" />
          </Box>
        </Box>

        <Box gap="xlarge" height={{ max: '440px' }} overflow="scroll">
          <Box data-testid="camera-selection-sheet__your-cameras">
            <Box margin={{ bottom: 'xsmall' }}>
              <SHDText style="subhead-all-caps" color="gray-900-alpha-50">
                Your cameras
              </SHDText>
            </Box>

            <Box direction="column" gap="xsmall">
              {userCaptures.map((capture) => {
                const status = handleGetStatus(capture);
                return (
                  <CameraSelector
                    key={capture._id}
                    status={status}
                    teamId={teamId}
                    teamName={teamName}
                    capture={capture}
                    onClick={(clickedCapture) =>
                      handleCameraSelected(clickedCapture)
                    }
                    isSelected={selectedCapture?._id === capture._id}
                  />
                );
              })}
            </Box>
          </Box>

          {teamCaptures.length > 0 && (
            <Box data-testid="camera-selection-sheet__team-cameras">
              <Box margin={{ bottom: 'xsmall' }}>
                <SHDText style="subhead-all-caps" color="gray-900-alpha-50">
                  Team Manager&apos;s cameras
                </SHDText>
              </Box>
              {teamCaptures.map((capture) => {
                const status = handleGetStatus(capture);
                return (
                  <CameraSelector
                    key={capture._id}
                    status={status}
                    teamId={teamId}
                    teamName={teamName}
                    capture={capture}
                    onClick={(clickedCapture) =>
                      handleCameraSelected(clickedCapture)
                    }
                    isSelected={selectedCapture?._id === capture._id}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </SHDModalContainer>
      <SHDFooter>
        <SHDModalContainer>
          <Box direction="row" gap="xsmall" margin={{ bottom: 'xsmall' }}>
            <CopyToClipboard
              text={`${UI}/${teamId}/live/`}
              onCopy={() => setCopied('stream link')}
            >
              <SHDButton
                secondary
                label="Stream link"
                icon={<ShareRounded />}
                reverse
                onClick={() => {}}
              />
            </CopyToClipboard>
            <CopyToClipboard
              text={scoringLink || ''}
              onCopy={() => setCopied('scoring link')}
            >
              <SHDButton
                secondary
                label="Scoring link"
                icon={<ShareRounded />}
                reverse
                onClick={() => {}}
              />
            </CopyToClipboard>
          </Box>

          <SHDButton
            primary
            label="Confirm"
            disabled={!selectedCapture}
            onClick={() =>
              handleConfirmCapture(selectedCapture as shd.TeamCapture)
            }
          />
        </SHDModalContainer>
      </SHDFooter>
      {copied !== null && (
        <Toast
          label={`Copied ${copied} to clipboard!`}
          background="status-ok"
          duration={3000}
          onClose={() => {
            setCopied(null);
          }}
        />
      )}
    </>
  );
};

export default CameraSelectionSheet;
